import { validarAssinaturaService } from './validar-assinatura.service';
import { renderPublicPageLayout } from '../public-pages.layout';
import {
    ValidarAssinaturaApiResponse,
    ValidarAssinaturaApiFunctionalResponse,
    ValidarAssinaturaHttpResult,
} from './validar-assinatura.model';

const template = require('html-loader!./validar-assinatura.html');

const TAMANHO_MAXIMO_ARQUIVO = 5 * 1024 * 1024; // 5MB em bytes

interface NormalizedResult {
    status: Exclude<ResultStatus, null>;
    message: string;
}

type ViewMode = 'upload' | 'result';
type ResultStatus = 'valid' | 'invalid' | null;

class ValidarAssinaturaPageView {
    private viewMode: ViewMode = 'upload';
    private resultStatus: ResultStatus = null;
    private resultMessage: string = '';
    private isSubmitting: boolean = false;
    private selectedFile: File | null = null;
    private fileError: string | null = null;

    render(): string {
        this.viewMode = 'upload';
        this.resultStatus = null;
        this.resultMessage = '';
        this.isSubmitting = false;
        this.selectedFile = null;
        this.fileError = null;

        return renderPublicPageLayout(template);
    }

    mount(): void {
        const input = document.getElementById('validar-assinatura-file') as HTMLInputElement | null;
        const button = document.getElementById('validar-assinatura-submit') as HTMLButtonElement | null;
        const resetButton = document.getElementById('validar-assinatura-reset') as HTMLButtonElement | null;

        if (!input || !button || !resetButton) {
            return;
        }

        input.addEventListener('change', () => this.handleFileChange(input));
        button.addEventListener('click', () => this.handleSubmit());
        resetButton.addEventListener('click', () => this.handleReset(input));
        this.updateView();
    }

    private handleFileChange(input: HTMLInputElement): void {
        const file = input.files && input.files.length > 0 ? input.files[0] : null;
        this.fileError = null;

        if (!file) {
            this.selectedFile = null;
            this.updateView();
            return;
        }

        if (!this.isPdfFile(file)) {
            this.fileError = 'O tipo do arquivo deve ser PDF.';
            this.clearSelectedFile(input);
            return;
        }

        if (file.size > TAMANHO_MAXIMO_ARQUIVO) {
            this.fileError = `O arquivo tem ${this.formatarTamanho(file.size)} e o tamanho máximo é `
                + `${this.formatarTamanho(TAMANHO_MAXIMO_ARQUIVO)}.`;
            this.clearSelectedFile(input);
            return;
        }

        this.selectedFile = file;
        this.updateView();
    }

    private clearSelectedFile(input: HTMLInputElement): void {
        this.selectedFile = null;
        input.value = '';
        this.updateView();
    }

    private async handleSubmit(): Promise<void> {
        if (!this.selectedFile || this.isSubmitting) {
            this.updateView();
            return;
        }

        this.isSubmitting = true;
        this.updateView();

        try {
            const response = await validarAssinaturaService.verificarAssinatura(this.selectedFile);
            const normalizedResult = this.normalizeVerifierResponse(response);

            this.viewMode = 'result';
            this.resultStatus = normalizedResult.status;
            this.resultMessage = normalizedResult.message;
        } catch (error) {
            this.viewMode = 'result';
            this.resultStatus = 'invalid';
            this.resultMessage = 'Não foi possível verificar a assinatura no momento.';
        } finally {
            this.isSubmitting = false;
            this.updateView();
        }
    }

    private handleReset(input: HTMLInputElement): void {
        this.viewMode = 'upload';
        this.resultStatus = null;
        this.resultMessage = '';
        this.isSubmitting = false;
        this.selectedFile = null;
        this.fileError = null;
        input.value = '';
        this.updateView();
    }

    private updateView(): void {
        const fileNameElement = document.getElementById('validar-assinatura-file-name');
        const uploadArea = document.getElementById('validar-assinatura-upload-area');
        const button = document.getElementById('validar-assinatura-submit') as HTMLButtonElement | null;
        const resultContainer = document.getElementById('validar-assinatura-result');
        const resultFileName = document.getElementById('validar-assinatura-result-file-name');
        const resultMessage = document.getElementById('validar-assinatura-result-message');
        const fileInput = document.getElementById('validar-assinatura-file') as HTMLInputElement | null;
        const fileError = document.getElementById('validar-assinatura-file-error');

        if (fileNameElement) {
            fileNameElement.textContent = this.selectedFile ? this.selectedFile.name : 'Nenhum documento selecionado';
            fileNameElement.setAttribute('style', this.selectedFile
                ? 'color: #0F172A; font-size:14px; line-height:1.5; font-weight: 600;'
                : 'color: #6B7280; font-size:14px; line-height:1.5;');
        }

        if (uploadArea) {
            uploadArea.style.display = this.viewMode === 'upload' ? 'block' : 'none';
        }

        if (button) {
            const enabled = !!this.selectedFile && !this.isSubmitting;
            button.disabled = !enabled;
            button.textContent = this.isSubmitting ? 'Verificando...' : 'Verificar certificado de assinatura';
            button.setAttribute('style', this.getSubmitButtonStyle(enabled));
        }

        if (resultContainer) {
            resultContainer.style.display = this.viewMode === 'result' ? 'block' : 'none';
        }

        if (fileInput) {
            fileInput.disabled = this.isSubmitting;
        }

        if (fileError) {
            fileError.textContent = this.fileError || '';
            fileError.setAttribute('style', this.getFileErrorStyle());
        }

        if (resultFileName) {
            resultFileName.textContent = this.viewMode === 'result' && this.selectedFile ? this.selectedFile.name : '';
        }

        if (resultMessage) {
            resultMessage.textContent = this.getResultMessage();
            resultMessage.setAttribute('style', this.getResultStyle());
        }
    }

    private getSubmitButtonStyle(enabled: boolean): string {
        if (this.viewMode !== 'upload') {
            return `
                display: none;
                margin: 0 auto;
                padding: 6px 20px;
                border-radius: 2rem;
                border: 1px solid #E5E5E5;
                background-color: #00469B;
                color: #FFFFFF;
                font-size: 14px;
                font-weight: 700;
                cursor: pointer;
                transition: background-color 0.2s ease;
            `;
        }

        if (enabled) {
            return `
                display: block;
                margin: 0 auto;
                padding: 6px 20px;
                border-radius: 2rem;
                border: 1px solid #E5E5E5;
                background-color: #00469B;
                color: #FFFFFF;
                font-size: 14px;
                font-weight: 700;
                cursor: pointer;
                transition: background-color 0.2s ease;
            `;
        }

        return `
            display: block;
            margin: 0 auto;
            padding: 6px 20px;
            border-radius: 2rem;
            border: 1px solid #E5E5E5;
            background-color: #9FB8CC;
            color: #FFFFFF;
            font-size: 14px;
            font-weight: 700;
            cursor: not-allowed;
            transition: background-color 0.2s ease;
        `;
    }

    private getFileErrorStyle(): string {
        if (!this.fileError) {
            return 'display: none;';
        }

        return `
            margin-top: 14px;
            padding: 10px 14px;
            border-radius: 14px;
            background-color: #FEF2F2;
            border: 1px solid #FECACA;
            color: #B91C1C;
            font-size: 13px;
            line-height: 1.5;
            text-align: center;
        `;
    }

    private getResultStyle(): string {
        if (this.resultStatus === 'invalid') {
            return `
                margin-top: 16px;
                padding: 14px 16px;
                border-radius: 14px;
                background-color: #FEF2F2;
                border: 1px solid #FECACA;
                color: #B91C1C;
                font-size: 14px;
                line-height: 1.6;
            `;
        }

        if (this.resultStatus === 'valid') {
            return `
                margin-top: 16px;
                padding: 14px 16px;
                border-radius: 14px;
                background-color: #ECFDF3;
                border: 1px solid #ABEFC6;
                color: #067647;
                font-size: 14px;
                line-height: 1.6;
            `;
        }

        return `
            display: none;
            padding: 14px 16px;
            border-radius: 14px;
            background-color: #F8FAFC;
            border: 1px solid #D9E6F2;
            color: #475569;
            font-size: 14px;
            line-height: 1.6;
        `;
    }

    private getResultMessage(): string {
        return this.resultMessage;
    }

    private normalizeVerifierResponse(response: ValidarAssinaturaHttpResult): NormalizedResult {
        if (this.isFunctionalResponse(response.body)) {
            const status = response.body.status === 'VALID' ? 'valid' : 'invalid';

            return {
                status,
                message: response.body.mensagem || this.getDefaultResultMessage(status)
            };
        }

        if (response.statusCode === 500) {
            return {
                status: 'invalid',
                message: 'O serviço de verificação está indisponível no momento.'
            };
        }

        return {
            status: 'invalid',
            message: 'Não foi possível verificar a assinatura no momento.'
        };
    }

    private isFunctionalResponse(response: ValidarAssinaturaApiResponse | null): response is ValidarAssinaturaApiFunctionalResponse {
        return !!response && 'status' in response;
    }

    private getDefaultResultMessage(status: Exclude<ResultStatus, null>): string {
        if (status === 'valid') {
            return 'A assinatura do documento é válida.';
        }

        return 'A assinatura do documento não é válida.';
    }

    private isPdfFile(file: File): boolean {
        return file.type === 'application/pdf' || /\.pdf$/i.test(file.name);
    }

    private formatarTamanho(bytes: number): string {
        const megabytes = parseFloat((bytes / 1024 / 1024).toFixed(2));

        return `${megabytes.toLocaleString('pt-BR', { maximumFractionDigits: 2 })} MB`;
    }
}

export const ValidarAssinaturaPage = new ValidarAssinaturaPageView();
